import { _, helpers, mui, React, ts, ui } from '_core';

import { TABLE_ROW_HEIGHT } from '@local/finsera-core/src/theme/layout-size';

import { Params } from './types';
import { radarChartFormat } from '../../common/formatters/radar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  fullScreen: boolean;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  returns: string;
  visualization: 'chart' | 'table';
  params: Params;
  title: string;
};

const Chart = ({ id, readOnly, fullScreen, loadWidgetData, returns, params, visualization, title }: ChartProps) => {
  const [data, setData] = React.useState<{
    chart: ts.types.widgets.RadarData;
    table: ts.types.widgets.TableData;
  }>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const dimensions =
    params.variant == 'histories' ? params.histories : params.return_horizons.map((rh) => rh.signature);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: returns,
          rename: { metric: 'spoke' },
        },
      });
      if (response) {
        setData(radarChartFormat(response.data));
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (returns) loadData();
    return () => setData(null);
  }, [returns]);

  const getColumns = React.useMemo(() => {
    if (!data?.table || !dimensions) return [];

    return ['spoke', ...dimensions].map((field) => ({
      field,
      headerName: field,
      flex: 1,
    })) as mui.dataGrid.GridColDef[];
  }, [data, dimensions]);

  return (
    <>
      <mui.core.Box display={visualization == 'chart' ? 'inline' : 'none'}>
        <ui.RadarChart
          id={id}
          data={data?.chart}
          labels={data?.table}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          dimensions={dimensions}
          fullScreen={fullScreen}
          readOnly={readOnly}
          exportTitle={title}
        />
      </mui.core.Box>
      <mui.core.Box display={visualization == 'table' ? 'inline' : 'none'}>
        {!_.isNull(data) && (
          <>
            <ui.FinDataGrid
              rows={data?.table || []}
              columns={getColumns}
              autoHeight
              hideFooter
              disableColumnMenu
              getRowId={(row) => row.spoke}
              disableColumnFilter
              rowHeight={TABLE_ROW_HEIGHT.sm}
              columnHeaderHeight={TABLE_ROW_HEIGHT.sm}
            />
          </>
        )}
      </mui.core.Box>
    </>
  );
};

export default Chart;
